import React from 'react';
import { FloatingLabelInput } from '../../../../../../../ui/components/Common/TextFields/OutlinedInput';
import {
  WellnessAssessmentType,
  WellnessConfig,
} from '../../../../../../../utils/types/wellness_types';
import { WELLNESS_ASSESSMENT_OPTIONS } from '../../../Constants';

interface SettingsTabProps {
  config: WellnessConfig;
  onChange: (updated: WellnessConfig) => void;
  onSave: () => void;
  isSaving: boolean;
}

const ASSESSMENT_DESCRIPTIONS: Record<WellnessAssessmentType, string> = {
  DASS21: 'Depression Anxiety Stress Scales',
  GAD7: 'Generalised Anxiety Disorder',
  PHQ9: 'Patient Health Questionnaire',
};

export const SettingsTab: React.FC<SettingsTabProps> = ({
  config,
  onChange,
  onSave,
  isSaving,
}) => {
  const handleDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const days = parseInt(e.target.value, 10);
    onChange({ ...config, dayLimit: isNaN(days) ? 0 : days });
  };

  const handleAssessmentToggle = (type: WellnessAssessmentType) => {
    const current = config.types;
    const next = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    onChange({ ...config, types: next });
  };

  const isValid =
    config.types.length > 0 && config.dayLimit >= 1;

  return (
    <div className='rounded-xl border border-slate-200 bg-white p-6 space-y-8'>

      { /* Days to Deadline */ }
      <div>
        <h3 className='text-sm font-extrabold text-slate-900 mb-1'>
          Wellness Screening Days to Deadline
        </h3>
        <p className='text-xs text-slate-500 mb-4'>
          Wellness invitation link expires{' '}
          <span className='font-semibold text-slate-700'>
            { config.dayLimit > 0 ? config.dayLimit : '—' }
          </span>{' '}
          day{ config.dayLimit !== 1 ? 's' : '' } after sending
        </p>
        <div className='max-w-[160px]'>
          <FloatingLabelInput
              label='Days'
              type='number'
              value={config.dayLimit > 0 ? String(config.dayLimit) : ''}
              onChange={handleDaysChange}
              placeholder='e.g. 7'
              error={config.dayLimit < 1 && config.dayLimit !== 0}
              helperText='Must be at least 1'
          />
        </div>
      </div>

      { /* Assessment Type Options */ }
      <div>
        <h3 className='text-sm font-extrabold text-slate-900 mb-1'>
          Wellness Screening Options
        </h3>
        <p className='text-xs text-slate-500 mb-4'>
          Select at least one assessment to include in the wellness screening
        </p>
        <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
          { WELLNESS_ASSESSMENT_OPTIONS.map((option) => {
            const type = option.value as WellnessAssessmentType;
            const checked = config.types.includes(type);
            return (
              <button
                  key={type}
                  type='button'
                  onClick={() => handleAssessmentToggle(type)}
                  className={`flex items-start gap-3 rounded-lg border p-4 text-left transition-colors ${
                    checked
                      ? 'border-emerald-400 bg-emerald-50'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                  }`}
              >
                { /* Custom checkbox */ }
                <span
                    className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                      checked
                        ? 'border-emerald-600 bg-emerald-600'
                        : 'border-slate-300 bg-white'
                    }`}
                >
                  { checked && (
                    <svg
                        className='h-2.5 w-2.5 text-white'
                        viewBox='0 0 10 8'
                        fill='none'
                    >
                      <path
                          d='M1 4l2.5 2.5L9 1'
                          stroke='currentColor'
                          strokeWidth='1.8'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                      />
                    </svg>
                  ) }
                </span>
                <span>
                  <span className='block text-sm font-bold text-slate-900'>
                    { type }
                  </span>
                  <span className='block text-xs text-slate-500 mt-0.5'>
                    { ASSESSMENT_DESCRIPTIONS[type] }
                  </span>
                </span>
              </button>
            );
          }) }
        </div>
      </div>

      { /* Save */ }
      <div className='flex justify-end'>
        <button
            type='button'
            onClick={onSave}
            disabled={!isValid || isSaving}
            className='inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50'
        >
          { isSaving ? (
            <>
              <svg className='h-4 w-4 animate-spin' fill='none' viewBox='0 0 24 24'>
                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
              </svg>
              Saving…
            </>
          ) : (
            'Save Settings'
          ) }
        </button>
      </div>
    </div>
  );
};
